/**
 * Componente para exibir benefícios dos planos Claro de forma visual
 */
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { MessageCircle, Navigation, Instagram, Facebook, Twitter, Shield, Globe, Smartphone } from 'lucide-react';
import type { PlanoCNPJ } from '@/constants/planosCNPJ';
import { useMegaBonus } from '@/hooks/useMegaBonus';
import { megaBonusGb } from '../../../../backend/src/services/whatsapp/gerar-mensagem-proposta';

interface BeneficiosPlanoProps {
  /** 59a — chips "✓ Nome" no lugar dos ícones (card de renovação) */
  chips?: boolean;
  plano: PlanoCNPJ;
  compact?: boolean;
}

const BENEFICIO_ICONES: Record<string, React.ReactNode> = {
  'WhatsApp ilimitado': <MessageCircle className="w-3 h-3" />,
  'Waze ilimitado': <Navigation className="w-3 h-3" />,
  'Instagram incluso': <Instagram className="w-3 h-3" />,
  'Facebook incluso': <Facebook className="w-3 h-3" />,
  'Messenger incluso': <MessageCircle className="w-3 h-3" />,
  'X (Twitter) incluso': <Twitter className="w-3 h-3" />,
  'Claro Monitor': <Shield className="w-3 h-3" />,
  'McAfee Security': <Shield className="w-3 h-3" />,
  'Claro Passaporte (roaming internacional)': <Globe className="w-3 h-3" />,
};

const BENEFICIO_CORES: Record<string, string> = {
  'WhatsApp ilimitado': 'bg-success/10 text-success border-success/40',
  'Waze ilimitado': 'bg-info/10 text-info border-info/40',
  'Instagram incluso': 'bg-muted text-brand-rose border-border',
  'Facebook incluso': 'bg-info/10 text-info border-info/40',
  'Messenger incluso': 'bg-info/10 text-info border-info/40',
  'X (Twitter) incluso': 'bg-muted text-foreground border-border',
  'Claro Monitor': 'bg-muted text-primary border-border',
  'McAfee Security': 'bg-warning/10 text-warning border-warning/40',
  'Claro Passaporte (roaming internacional)': 'bg-warning/10 text-warning border-warning/40',
};

export function BeneficiosPlano({ plano, compact = false, chips = false }: BeneficiosPlanoProps) {
  const beneficios = Array.isArray(plano.beneficios) ? plano.beneficios : [];
  // O plano diz se recebe o Mega Bônus; o número é do banco (Planos e Preços).
  const megabonus = useMegaBonus().data?.gb ?? megaBonusGb();

  if (chips) {
    const lista = Array.isArray(plano.beneficios) ? plano.beneficios : [];
    if (lista.length === 0) return null;
    return (
      <span className="flex flex-wrap items-center gap-1.5">
        {lista.map((b: string) => (
          <span key={b} className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2 py-0.5 text-[11px] text-foreground">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="h-2.5 w-2.5 text-[#075E54] dark:text-[#25D366]"><path d="M20 6 9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" /></svg>
            {b.replace(' ilimitado', '').replace(' incluso', '')}
          </span>
        ))}
      </span>
    );
  }

  if (compact) {
    // Versão compacta: apenas ícones principais
    const beneficiosPrincipais = [
      'WhatsApp ilimitado',
      'Instagram incluso',
      'Facebook incluso',
    ];
    
    return (
      <TooltipProvider>
        <div className="flex items-center gap-1">
          {beneficiosPrincipais.map((beneficio) => {
            if (!beneficios.includes(beneficio)) return null;
            return (
              <Tooltip key={beneficio}>
                <TooltipTrigger asChild>
                  <span className="p-1 rounded bg-muted text-muted-foreground">
                    {BENEFICIO_ICONES[beneficio]}
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs">{beneficio}</p>
                </TooltipContent>
              </Tooltip>
            );
          })}
          {plano.passaporte && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="p-1 rounded bg-warning/10 text-warning">
                  <Globe className="w-3 h-3" />
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-xs">Claro Passaporte (roaming)</p>
              </TooltipContent>
            </Tooltip>
          )}
          {plano.megabonus && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge variant="outline" className="border-border bg-card px-1 py-0 text-[10px] text-foreground">
                  <span className="font-bold text-[#075E54] dark:text-[#25D366]">+{megabonus}GB</span>
                </Badge>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-xs">Megabônus com Claro Fibra</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </TooltipProvider>
    );
  }

  // Versão completa: todos os benefícios em badges
  return (
    <div className="flex flex-wrap gap-1">
      {beneficios.map((beneficio) => (
        <Badge
          key={beneficio}
          variant="outline"
          className={`text-[10px] px-1.5 py-0.5 ${BENEFICIO_CORES[beneficio] || 'bg-muted text-muted-foreground'}`}
        >
          {BENEFICIO_ICONES[beneficio]}
          <span className="ml-1">{beneficio.split(' ')[0]}</span>
        </Badge>
      ))}
      {plano.megabonus && (
        <Badge variant="outline" className="border-border bg-card px-1.5 py-0.5 text-[10px] text-foreground">
          <Smartphone className="mr-1 h-3 w-3 text-muted-foreground" />
          <span className="font-bold text-[#075E54] dark:text-[#25D366]">+{megabonus}GB</span>&nbsp;c/ Fibra
        </Badge>
      )}
    </div>
  );
}

// Mini cards de benefícios para exibir abaixo do total
export function BeneficiosResumo() {
  return (
    <div className="grid grid-cols-4 gap-2 mt-3 p-2 rounded-lg bg-muted/50">
      <div className="flex flex-col items-center text-center">
        <div className="p-1.5 rounded-full bg-success/10 text-success mb-1">
          <MessageCircle className="w-3.5 h-3.5" />
        </div>
        <span className="text-[10px] text-muted-foreground">WhatsApp</span>
      </div>
      <div className="flex flex-col items-center text-center">
        <div className="p-1.5 rounded-full bg-muted text-brand-rose mb-1">
          <Instagram className="w-3.5 h-3.5" />
        </div>
        <span className="text-[10px] text-muted-foreground">Instagram</span>
      </div>
      <div className="flex flex-col items-center text-center">
        <div className="p-1.5 rounded-full bg-info/10 text-info mb-1">
          <Facebook className="w-3.5 h-3.5" />
        </div>
        <span className="text-[10px] text-muted-foreground">Facebook</span>
      </div>
      <div className="flex flex-col items-center text-center">
        <div className="p-1.5 rounded-full bg-muted text-primary mb-1">
          <Shield className="w-3.5 h-3.5" />
        </div>
        <span className="text-[10px] text-muted-foreground">Segurança</span>
      </div>
    </div>
  );
}
